import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { salesControllingApi } from '../../services/salesControllingApi';
import { useToast } from '../ui/Toast';
import type { SCFilters, SCFilterOptions, SalesOverviewPeriod, SalesOverviewSummary } from '../../types/salesControlling';

interface Props {
    filters: SCFilters;
    filterOptions: SCFilterOptions;
    refreshKey: number;
}

export function SalesOverviewTab({ filters, filterOptions, refreshKey }: Props) {
    const { toast } = useToast();
    const [periods, setPeriods] = useState<SalesOverviewPeriod[]>([]);
    const [summary, setSummary] = useState<SalesOverviewSummary>({
        totalNetSalesEUR: 0,
        totalGrossProfitEUR: 0,
        avgMarginPct: 0,
        totalInvoiceCount: 0,
    });
    const [loading, setLoading] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [granularity, setGranularity] = useState<'month' | 'quarter' | 'year'>('month');
    const [businessUnit, setBusinessUnit] = useState('');
    const [salesManagerFilter, setSalesManagerFilter] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await salesControllingApi.getSalesOverview({
                company: filters.company,
                year,
                businessUnit,
                salesManagerID: salesManagerFilter,
                granularity,
            });
            setPeriods(result.periods);
            setSummary(result.summary);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    }, [filters.company, year, businessUnit, salesManagerFilter, granularity, toast]);

    useEffect(() => {
        loadData();
    }, [loadData, refreshKey]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('de-CH', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

    return (
        <div className="space-y-6">
            {/* Additional Filters */}
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500">Year</label>
                    <select
                        value={year}
                        onChange={e => setYear(parseInt(e.target.value, 10))}
                        className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    >
                        {years.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500">Granularity</label>
                    <div className="flex border rounded-md overflow-hidden">
                        {(['month', 'quarter', 'year'] as const).map(g => (
                            <button
                                key={g}
                                onClick={() => setGranularity(g)}
                                className={`px-3 py-1.5 text-sm ${granularity === g ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {g.charAt(0).toUpperCase() + g.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500">Business Unit</label>
                    <select
                        value={businessUnit}
                        onChange={e => setBusinessUnit(e.target.value)}
                        className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    >
                        <option value="">All</option>
                        {filterOptions.businessUnits.map(bu => (
                            <option key={bu} value={bu}>{bu}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-500">Sales Manager</label>
                    <select
                        value={salesManagerFilter}
                        onChange={e => setSalesManagerFilter(e.target.value)}
                        className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    >
                        <option value="">All</option>
                        {filterOptions.salesManagers.map(sm => (
                            <option key={sm.id} value={sm.id}>{sm.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
                <SummaryCard label="Net Sales" value={`${formatCurrency(summary.totalNetSalesEUR)} EUR`} />
                <SummaryCard label="Gross Profit" value={`${formatCurrency(summary.totalGrossProfitEUR)} EUR`} />
                <SummaryCard label="Avg Margin" value={`${summary.avgMarginPct.toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`} highlight={summary.avgMarginPct < 5} />
                <SummaryCard label="Invoices" value={formatCurrency(summary.totalInvoiceCount)} />
            </div>

            {/* Chart */}
            {!loading && periods.length > 0 && (
                <div className="bg-white border rounded-lg p-4">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={periods}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis
                                yAxisId="amount"
                                tick={{ fontSize: 12 }}
                                tickFormatter={v => formatCurrency(v)}
                            />
                            <YAxis
                                yAxisId="pct"
                                orientation="right"
                                tick={{ fontSize: 12 }}
                                tickFormatter={v => `${v}%`}
                            />
                            <Tooltip
                                formatter={(value, name) => {
                                    const v = Number(value);
                                    if (name === 'Margin %') return [`${v.toFixed(1)}%`, name];
                                    return [`${formatCurrency(v)} EUR`, name];
                                }}
                            />
                            <Legend />
                            <Line yAxisId="amount" type="monotone" dataKey="netSalesEUR" name="Net Sales" stroke="#3b82f6" strokeWidth={2} dot={false} />
                            <Line yAxisId="amount" type="monotone" dataKey="grossProfitEUR" name="Gross Profit" stroke="#10b981" strokeWidth={2} dot={false} />
                            <Line yAxisId="pct" type="monotone" dataKey="marginPct" name="Margin %" stroke="#f59e0b" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {loading && (
                <div className="text-center text-gray-400 py-8">Loading...</div>
            )}

            {/* Data Table */}
            {!loading && periods.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b">
                                <th className="py-2 px-3">Period</th>
                                <th className="py-2 px-3 text-right">Net Sales (EUR)</th>
                                <th className="py-2 px-3 text-right">Gross Profit (EUR)</th>
                                <th className="py-2 px-3 text-right">Margin %</th>
                                <th className="py-2 px-3 text-right">Invoices</th>
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map(p => (
                                <tr key={p.period} className="border-b hover:bg-gray-50">
                                    <td className="py-2 px-3 font-medium">{p.period}</td>
                                    <td className="py-2 px-3 text-right">{formatCurrency(p.netSalesEUR)}</td>
                                    <td className="py-2 px-3 text-right">{formatCurrency(p.grossProfitEUR)}</td>
                                    <td className={`py-2 px-3 text-right font-medium ${p.marginPct < 5 ? 'text-amber-600' : 'text-green-600'}`}>
                                        {p.marginPct.toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                                    </td>
                                    <td className="py-2 px-3 text-right">{p.invoiceCount}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 font-semibold">
                                <td className="py-2 px-3">Total</td>
                                <td className="py-2 px-3 text-right">{formatCurrency(summary.totalNetSalesEUR)}</td>
                                <td className="py-2 px-3 text-right">{formatCurrency(summary.totalGrossProfitEUR)}</td>
                                <td className={`py-2 px-3 text-right ${summary.avgMarginPct < 5 ? 'text-amber-600' : 'text-green-600'}`}>
                                    {summary.avgMarginPct.toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                                </td>
                                <td className="py-2 px-3 text-right">{summary.totalInvoiceCount}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div className={`border rounded-lg p-4 ${highlight ? 'border-amber-300 bg-amber-50' : ''}`}>
            <div className="text-xs text-gray-500 font-medium">{label}</div>
            <div className={`text-xl font-bold mt-1 ${highlight ? 'text-amber-600' : 'text-gray-900'}`}>{value}</div>
        </div>
    );
}
